import React, { useState, useEffect, useRef } from "react";
import "./App.css";

// NOTE: APP_VERSION is auto-managed by build-main-bitcoin-price-date.sh using version.txt
const APP_VERSION = "BitcoinAgree Price By Date V0.3.18"; // do not edit manually; change version.txt instead
const THEME_KEY = "bitcoinagree_theme";

function getCookie(name) {
  const parts = document.cookie.split(";").map(function(part) { return part.trim(); });
  for (let i = 0; i < parts.length; i++) {
    if (parts[i].indexOf(name + "=") === 0) return decodeURIComponent(parts[i].substring(name.length + 1));
  }
  return "";
}

function getCookieDomain() {
  const host = window.location.hostname;
  if (host === "bitcoinagree.com" || host.endsWith(".bitcoinagree.com")) return ".bitcoinagree.com";
  if (host === "test.local" || host.endsWith(".test.local")) return ".test.local";
  return "";
}

function getStoredThemeMode() {
  const cookieMode = getCookie(THEME_KEY);
  if (cookieMode === "auto" || cookieMode === "light" || cookieMode === "dark") return cookieMode;
  try {
    const localMode = window.localStorage.getItem(THEME_KEY);
    if (localMode === "auto" || localMode === "light" || localMode === "dark") return localMode;
  } catch(e) {}
  return "auto";
}

function resolveTheme(mode) {
  if (mode === "light" || mode === "dark") return mode;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch(e) {
    return "light";
  }
}

function saveThemeMode(mode) {
  try { window.localStorage.setItem(THEME_KEY, mode); } catch(e) {}
  const domain = getCookieDomain();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const domainPart = domain ? "; Domain=" + domain : "";
  document.cookie = THEME_KEY + "=" + encodeURIComponent(mode) + "; Max-Age=31536000; Path=/; SameSite=Lax" + domainPart + secure;
}

function getThemeTokens(theme) {
  if (theme === "dark") {
    return {
      bg: "#101418",
      surface: "#171d24",
      surfaceSoft: "#202832",
      text: "#edf2f7",
      muted: "#a8b3c2",
      border: "#334152",
      bitcoin: "#f7a531",
      bitcoinStrong: "#ffc46b",
      danger: "#ff8a80"
    };
  }
  return {
    bg: "#f6f8fb",
    surface: "#ffffff",
    surfaceSoft: "#f8fafc",
    text: "#17202a",
    muted: "#52616f",
    border: "#d8e0ea",
    bitcoin: "#f7931a",
    bitcoinStrong: "#b96000",
    danger: "#b42318"
  };
}

function getHomeHref() {
  try {
    const h = window.location.host;
    if(h.endsWith(".test.local") || h === "test.local" || h.startsWith("localhost")) return "http://test.local/";
  } catch(e){}
  return "https://bitcoinagree.com/";
}

function getThemeHref() {
  try {
    const h = window.location.host;
    if(h.endsWith(".test.local") || h === "test.local" || h.startsWith("localhost")) return "http://theme.test.local/";
  } catch(e){}
  return "https://theme.bitcoinagree.com/";
}

function getApiBase() {
  try {
    const h = window.location.host;
    if(h.endsWith(".test.local") || h === "test.local" || h.startsWith("localhost") || h.startsWith("127.0.0.1")) return "http://bitcoin.api.test.local/v1/";
  } catch(e){}
  return "https://bitcoin.api.bitcoinagree.com/v1/";
}

function SiteFooter(props) {
  const tokens = props.tokens;
  const footerLinkStyle = {
    color: tokens.bitcoinStrong,
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    textDecorationColor: tokens.border,
    textUnderlineOffset: 3
  };
  const groups = [
    {
      title: "Services",
      links: [
        { href: getHomeHref(), label: "BitcoinAgree" },
        { href: getThemeHref(), label: "Theme Lab" }
      ]
    },
    {
      title: "Community",
      links: [
        { href: "https://www.reddit.com/r/bitcoinagree/", label: "Reddit", external: true },
        { href: "https://x.com/bitcoinagree", label: "X (Twitter)", external: true },
        { href: "https://www.youtube.com/@bitcoinagree", label: "YouTube", external: true },
        { href: "https://github.com/bitcoinagree", label: "GitHub", external: true }
      ]
    },
    {
      title: "Info",
      links: [
        { href: "", label: "Privacy Policy" },
        { href: "", label: "Terms of Service" },
        { href: "", label: "Contact" }
      ]
    }
  ];

  return (
    <footer className="site-footer" style={{ color: tokens.muted, borderTop: "1px solid " + tokens.border }}>
      <div className="site-footer-grid">
        {groups.map(function(group) {
          return (
            <div className="site-footer-group" key={group.title}>
              <div className="site-footer-title" style={{ color: tokens.text }}>{group.title}</div>
              <div className="site-footer-links">
                {group.links.map(function(item) {
                  return (
                    <a
                      href={item.href}
                      key={item.label}
                      style={footerLinkStyle}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                    >
                      {item.label}
                    </a>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="site-footer-bottom">
        <div className="site-footer-nostr" style={{ color: tokens.bitcoinStrong }}>
          Find me on Nostr: bitcoinagree@bitcoinagree.com
        </div>
        <a
          href="https://primal.net/p/nprofile1qqsgquzttv6xac0ft4f7lspqsdfkg4dmalz2f5hpyc59c2yk5qv0xacrp8zm5"
          target="_blank"
          rel="noopener noreferrer"
          style={footerLinkStyle}
        >
          Primal
        </a>
        <div className="site-footer-made">
          Made with 🧡 by <span style={{ color: tokens.bitcoinStrong, fontWeight: 700 }}>BitcoinAgree</span>
        </div>
      </div>
    </footer>
  );
}

function toUnixTimestamp(dateStr, timeStr) {
  if (!dateStr) return null;
  const iso = timeStr ? `${dateStr}T${timeStr}:00` : `${dateStr}T00:00:00`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.floor(d.getTime() / 1000);
}

export default function App() {
  // Default date to today (local) on mount
  const todayStrRef = useRef(null);
  if (todayStrRef.current === null) {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    todayStrRef.current = `${yyyy}-${mm}-${dd}`;
  }

  const [date, setDate] = useState(todayStrRef.current); // initialize to today
  const [time, setTime] = useState(""); // optional time left empty
  const [btc, setBtc] = useState("1"); // raw numeric string without commas
  const [usd, setUsd] = useState(""); // raw numeric string without commas
  const [btcFocused, setBtcFocused] = useState(false);
  const [usdFocused, setUsdFocused] = useState(false);
  const [price, setPrice] = useState(null); // price per 1 BTC
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resolvedTs, setResolvedTs] = useState(null);
  const [lastEdited, setLastEdited] = useState("btc"); // track which side user edited last
  const [themeMode, setThemeMode] = useState(getStoredThemeMode());
  const [resolvedTheme, setResolvedTheme] = useState(resolveTheme(themeMode));

  // Fetch price whenever date or time changes (debounced)
  useEffect(() => {
    if (!date) return;
    const ts = toUnixTimestamp(date, time);
    if (!ts) {
      setError("Invalid date/time");
      setPrice(null);
      setResolvedTs(null);
      return;
    }
    let cancelled = false;
    setError(null);
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`${getApiBase()}?time=${ts}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.usd == null) throw new Error("No price in response");
        if (cancelled) return;
        setPrice(data.usd);
        setResolvedTs(data.timestamp);
      } catch (e) {
        if (cancelled) return;
        setError(e.message);
        setPrice(null);
        setResolvedTs(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400); // debounce 400ms
    return () => { cancelled = true; clearTimeout(handle); };
  }, [date, time]);

  // When price arrives or changes, recompute the opposite field based on last edited
  useEffect(() => {
    if (price == null) return;
    if (lastEdited === 'btc') {
      const btcNum = parseFloat(btc || '0');
      if (!isNaN(btcNum)) {
        const usdVal = btcNum * price;
        setUsd(usdVal ? String(usdVal.toFixed(2)) : '');
      }
    } else if (lastEdited === 'usd') {
      const usdNum = parseFloat(usd || '0');
      if (!isNaN(usdNum) && price) {
        const btcVal = usdNum / price;
        // trim trailing zeros
        let btcStr = btcVal.toFixed(8).replace(/0+$/,'').replace(/\.$/,'');
        setBtc(btcStr || '0');
      }
    }
  }, [price]);

  function stripCommas(v) { return v.replace(/,/g,''); }
  function formatWithCommas(v, opts) {
    if (v === '' || v === '.' || v === '-') return v; // partial
    const num = Number(v);
    if (isNaN(num)) return v; // fallback
    return num.toLocaleString(undefined, opts);
  }
  function normalizeNumericInput(v, allowDecimals=true) {
    v = v.trim();
    if (v === '') return '';
    v = stripCommas(v);
    // Allow only digits and optional single dot
    if (allowDecimals) {
      if (!/^\d*(\.\d*)?$/.test(v)) return v; // keep raw so user can adjust
    } else {
      if (!/^\d*$/.test(v)) return v;
    }
    return v;
  }

  const onBtcChange = (e) => {
    const raw = normalizeNumericInput(e.target.value, true);
    setBtc(raw);
    setLastEdited('btc');
    if (price != null) {
      const btcNum = parseFloat(stripCommas(raw) || '0');
      if (!isNaN(btcNum)) {
        const usdVal = btcNum * price;
        setUsd(usdVal ? String(usdVal.toFixed(2)) : '');
      }
    }
  };

  const onUsdChange = (e) => {
    const raw = normalizeNumericInput(e.target.value, true);
    setUsd(raw);
    setLastEdited('usd');
    if (price != null) {
      const usdNum = parseFloat(stripCommas(raw) || '0');
      if (!isNaN(usdNum) && price) {
        const btcVal = usdNum / price;
        let btcStr = btcVal.toFixed(8).replace(/0+$/,'').replace(/\.$/,'');
        setBtc(btcStr || '0');
      }
    }
  };

  const versionShort = (APP_VERSION.match(/V[0-9]+\.[0-9]+\.[0-9]+/) || [""])[0];

  // Set browser tab title with version
  useEffect(() => {
    document.title = `Bitcoin Price by Date ${versionShort}`.trim();
  }, [versionShort]);

  useEffect(() => {
    function apply() {
      const nextTheme = resolveTheme(themeMode);
      setResolvedTheme(nextTheme);
      document.documentElement.setAttribute("data-theme", nextTheme);
      document.documentElement.setAttribute("data-theme-mode", themeMode);
      saveThemeMode(themeMode);
    }
    apply();
    let media;
    try {
      media = window.matchMedia("(prefers-color-scheme: dark)");
      if (themeMode === "auto") {
        if (media.addEventListener) media.addEventListener("change", apply);
        else if (media.addListener) media.addListener(apply);
      }
    } catch(e) {}
    return () => {
      if (media && themeMode === "auto") {
        if (media.removeEventListener) media.removeEventListener("change", apply);
        else if (media.removeListener) media.removeListener(apply);
      }
    };
  }, [themeMode]);

  useEffect(() => {
    function syncThemeMode(event) {
      if (event && event.type === "storage" && event.key !== THEME_KEY) return;
      const nextMode = getStoredThemeMode();
      setThemeMode(function(currentMode) {
        return currentMode === nextMode ? currentMode : nextMode;
      });
    }
    window.addEventListener("storage", syncThemeMode);
    window.addEventListener("focus", syncThemeMode);
    return function() {
      window.removeEventListener("storage", syncThemeMode);
      window.removeEventListener("focus", syncThemeMode);
    };
  }, []);

  const tokens = getThemeTokens(resolvedTheme);
  const modes = ["auto", "light", "dark"];
  const buttonStyle = {border:"1px solid " + tokens.bitcoin, borderRadius:6, background:tokens.surfaceSoft, color:tokens.bitcoinStrong, cursor:"pointer", fontSize:14, fontWeight:600, padding:"9px 12px"};
  const activeButtonStyle = Object.assign({}, buttonStyle, {background:tokens.bitcoin, color:"#111827", fontWeight:700});
  const modeButtonStyle = Object.assign({}, buttonStyle, {alignItems:"center", boxSizing:"border-box", display:"inline-flex", height:44, justifyContent:"center", padding:0, width:68});
  const activeModeButtonStyle = Object.assign({}, modeButtonStyle, {background:tokens.bitcoin, color:"#111827", fontWeight:700});
  const inputStyle = {width:"100%", maxWidth:"100%", fontSize:17, padding:"9px 10px", border:"1px solid " + tokens.border, borderRadius:6, background:tokens.surface, color:tokens.text};
  const toolPanelStyle = {border:"1px solid " + tokens.border, borderRadius:8, padding:18, background:tokens.surface};
  const panelStyle = {border:"1px solid " + tokens.border, borderRadius:8, padding:16, background:tokens.surfaceSoft};
  const fieldStyle = {display:"flex", flexDirection:"column", fontSize:14, minWidth:0};
  const domainName = (function(){ try { return window.location.hostname || "price-date.bitcoinagree.com"; } catch(e) { return "price-date.bitcoinagree.com"; } })();

  return (
    <div style={{minHeight:"100vh", background:tokens.bg, color:tokens.text, fontFamily:"Arial, sans-serif", padding:"28px 16px 32px"}}>
    <div style={{maxWidth:900, margin:"0 auto"}}>
      <header className="site-header">
        <div>
          <a href={getHomeHref()} style={{color:tokens.bitcoinStrong, textDecoration:"none", fontWeight:700}}>BitcoinAgree</a>
          <h1 className="site-title">Bitcoin Price by Date</h1>
          <div style={{fontSize:13, color:tokens.muted}}>{domainName}</div>
        </div>
        <div className="theme-mode-control">
          {modes.map(function(mode) {
            return <button key={mode} type="button" style={themeMode === mode ? activeModeButtonStyle : modeButtonStyle} onClick={function(){ setThemeMode(mode); }}>{mode.charAt(0).toUpperCase() + mode.slice(1)}</button>;
          })}
        </div>
      </header>
      <div className="tool-card" style={toolPanelStyle}>
      <div style={{display:"flex", flexDirection:"column", gap:16}}>
        {/* Row 1: Date + Time */}
        <div className="tool-field-grid" style={panelStyle}>
          <label style={fieldStyle}>
            Date
            <input style={inputStyle} type="date" value={date} onChange={e=>setDate(e.target.value)} />
          </label>
          <label style={fieldStyle}>
            Time
            <input style={inputStyle} type="time" value={time} onChange={e=>setTime(e.target.value)} />
          </label>
        </div>
        {/* Row 2: BTC + USD */}
        <div className="tool-field-grid" style={panelStyle}>
          <label style={fieldStyle}>
            BTC
            <input
              style={inputStyle}
              type="text"
              inputMode="decimal"
              value={btcFocused ? btc : formatWithCommas(btc, {maximumFractionDigits:8})}
              onChange={onBtcChange}
              onFocus={()=>setBtcFocused(true)}
              onBlur={()=>setBtcFocused(false)}
            />
          </label>
          <label style={fieldStyle}>
            USD
            <input
              style={inputStyle}
              type="text"
              inputMode="decimal"
              value={usdFocused ? usd : formatWithCommas(usd, {minimumFractionDigits:2, maximumFractionDigits:2})}
              onChange={onUsdChange}
              onFocus={()=>setUsdFocused(true)}
              onBlur={()=>setUsdFocused(false)}
            />
          </label>
        </div>
        {/* Status / meta */}
        <div style={panelStyle}>
        <div style={{
          minHeight:32,
          display:"flex",
          flexDirection:"column",
          gap:4,
          textAlign:"left",
          width:"100%"
        }}>
          <div>
            {loading && <span style={{fontSize:12, color:tokens.muted}}>Fetching price...</span>}
            {error && <span style={{color:tokens.danger, fontSize:12}}>Error: {error}</span>}
            {!loading && !error && resolvedTs && price != null && (
              <span style={{fontSize:11, color:tokens.muted}}>1 BTC = ${price.toLocaleString()} at {new Date(resolvedTs*1000).toLocaleString()}</span>
            )}
          </div>
          <div style={{fontSize:11, color:tokens.muted, lineHeight:1.3}}>
            <div>Use this tool to:</div>
            <div>1) Get BTC amount from a USD value at a past date/time.</div>
            <div>2) Get USD value of a BTC amount at that past date/time.</div>
          </div>
        </div>
        </div>
      </div>
      </div>
      <SiteFooter tokens={tokens} />
    </div>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/App.js